import {apiFetch as fetch} from './sessionInstance';
// Web / Telegram photo picking via a plain file input. On phones the browser offers
// camera or library; `capture` asks for the camera directly. Re-encoding through a canvas
// resizes to 1600 px and drops all EXIF (including location). Photos are kept in IndexedDB
// (idb-keyval); signed-in library sync backs compressed photos up to private cloud storage.
import { useEffect, useState } from 'react';
import { del, get, set, keys } from 'idb-keyval';
import { nanoid } from 'nanoid/non-secure';
import type { MediaRef } from '../types';

export type PickedPhoto = { mimeType?: string; uri: string; width?: number; height?: number; blob?: Blob };

const MAX = 1600;
const QUALITY = 0.75;

function chooseFile(capture: boolean): Promise<File | null> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (capture) input.setAttribute('capture', 'environment');
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.addEventListener('cancel', () => resolve(null));
    input.click();
  });
}

async function resize(file: File): Promise<PickedPhoto> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', QUALITY),
  );
  return { uri: URL.createObjectURL(blob), width, height, blob };
}

export async function takePhoto(): Promise<PickedPhoto | null> {
  const file = await chooseFile(true);
  return file ? resize(file) : null;
}

export async function pickPhoto(): Promise<PickedPhoto | null> {
  const file = await chooseFile(false);
  return file ? resize(file) : null;
}

export async function savePhoto(photo: PickedPhoto): Promise<MediaRef> {
  const key = `photo-${nanoid()}`;
  const blob = photo.blob ?? (await (await fetch(photo.uri)).blob());
  await set(key, blob);
  return { uri: `idb:${key}`, width: photo.width, height: photo.height, mimeType: photo.mimeType ?? blob.type };
}

export async function deletePhoto(ref: MediaRef): Promise<void> {
  if (ref.uri.startsWith('idb:')) {
    const key=ref.uri.slice(4), url=cache.get(ref.uri);
    if (url) URL.revokeObjectURL(url);
    cache.delete(ref.uri);
    await del(key).catch(() => {});
  }
}

const cache = new Map<string, string>();

export function usePhotoUri(ref: MediaRef | undefined): string | null {
  const key = ref?.uri;
  const [uri, setUri] = useState<string | null>(() => (key && !key.startsWith('idb:') && !key.startsWith('chefi-asset:') ? key : key ? cache.get(key) ?? null : null));
  useEffect(() => {
    if (!key) return setUri(null);
    if(key.startsWith('chefi-asset:')){
      let alive=true;
      import('./assetBackup').then(m=>m.downloadPhoto(ref!)).then(async saved=>{
        const blob=await get<Blob>(saved.uri.slice(4));if(!blob||!alive)return;
        const url=URL.createObjectURL(blob);cache.set(key,url);setUri(url);
      }).catch(()=>{if(alive)setUri(null);});
      return ()=>{alive=false;};
    }
    if (!key.startsWith('idb:')) return setUri(key);
    const hit = cache.get(key);
    if (hit) return setUri(hit);
    let alive = true;
    get<Blob>(key.slice(4))
      .then(blob => {
        if (!blob || !alive) return;
        const url = URL.createObjectURL(blob);
        cache.set(key, url);
        setUri(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [key,ref]);
  return uri;
}


/** Durable local receipt; a relay acknowledgement is sent only after this resolves. */
export async function cacheRemotePhoto(source: { uri: string; headers?: Record<string, string> }, mimeType?: string | null): Promise<MediaRef> {
  const response = await fetch(source.uri, { headers: source.headers, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`media_download_${response.status}`);
  const blob = await response.blob();
  return savePhoto({ uri: source.uri, blob, mimeType: mimeType ?? blob.type });
}
export async function remotePhotoData(source: { uri: string; headers?: Record<string, string> }, _mimeType?: string | null): Promise<string> {
  const response = await fetch(source.uri, { headers: source.headers, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`media_download_${response.status}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = reject; reader.readAsDataURL(blob);
  });
}

export async function storedPhoto(ref: MediaRef): Promise<PickedPhoto> {
  if (!ref.uri.startsWith('idb:')) return { ...ref };
  const blob = await get<Blob>(ref.uri.slice(4));
  if (!blob) throw new Error('local_media_missing');
  return { ...ref, blob };
}


export async function pickAttachment(): Promise<PickedPhoto | null> {
  return pickPhoto();
}

export async function localMediaData(ref: MediaRef): Promise<string> {
  const photo = await storedPhoto(ref);
  const blob = photo.blob;
  if (!blob) throw new Error('not_local_media');
  return new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = reject; reader.readAsDataURL(blob);
  });
}

export function releasePickedPhoto(photo: PickedPhoto): void { if (photo.uri.startsWith('blob:')) URL.revokeObjectURL(photo.uri); }

export async function eraseLocalPhotos():Promise<void> {
  for(const key of await keys()) if(typeof key==='string'&&key.startsWith('photo-')) await del(key);
  for(const url of cache.values()) URL.revokeObjectURL(url);
  cache.clear();
}

export async function hasLocalPhoto(ref:MediaRef):Promise<boolean>{return ref.uri.startsWith('idb:')?!!await get(ref.uri.slice(4)):false;}
